"use client";
import React from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { apiFetch } from '@/lib/api';
import PageCard from '@/components/ui/PageCard';
import MiniTalentCard from '@/components/common/MiniTalentCard';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import PolicyBanner, { LiveAnnouncer } from '@/components/messaging/SenderBanners';
import { titleFor, LIMIT_RESET_TEXT } from '@/components/messaging/strings';
import { normalizeSlug } from '@/lib/seo/slug';
import { gaEvent } from '@/lib/tracking';

const API_BASE = ((process.env.NEXT_PUBLIC_API_BASE_HTTP || process.env.API_BASE_HTTP || process.env.NEXT_PUBLIC_API_BASE_URL || '')
  .trim().replace(/\s+/g, '').replace(/\/$/, '')) || 'http://localhost:5151';

export default function ContactClient({ searchParams, personId, context, contextId }: { searchParams?: Promise<{ to?: string }>, personId?: string, context?: string, contextId?: string }) {
  const params = useSearchParams();
  const router = useRouter();
  const toParam = personId || params.get('to');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [recipient, setRecipient] = React.useState<any | null>(null);
  const [message, setMessage] = React.useState('Hello, I saw your profile on juicystarz and included my contact info below...');
  const [sent, setSent] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [cooldownHours, setCooldownHours] = React.useState<number>(0);
  const [cooldownUntil, setCooldownUntil] = React.useState<number | null>(null);
  const [cooldownLeft, setCooldownLeft] = React.useState<number>(0); // seconds
  const [monthlyRemaining, setMonthlyRemaining] = React.useState<number>(9999);
  const [errorCode, setErrorCode] = React.useState<null | 'email_disabled' | 'post_only' | 'friends_only' | 'blocked' | 'message_limit' | 'direct_cooldown' | 'recipient_not_approved'>(null);
  const [retryAfterHours, setRetryAfterHours] = React.useState<number>(0);
  const [rateLimitedMsg, setRateLimitedMsg] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (sent) {
      try {
        setTimeout(() => {
          const el = document.getElementById('contact-success-banner');
          (el as any)?.focus?.();
        }, 0);
      } catch {}
    }
  }, [sent]);

  React.useEffect(() => {
    (async () => {
      const to = toParam;
      if (!to) { router.push('/account/register?missid'); return; }
      // Normalize QS to path route for canonical URL
      if (!personId && typeof window !== 'undefined') {
        router.replace(`/contact/${encodeURIComponent(String(to))}`);
      }
      try {
        const res = await apiFetch(`/api/contact/${encodeURIComponent(String(to))}`, { method: 'GET', cache: 'no-store' });
        if (res.status === 401) { router.push(`/login?returnTo=${encodeURIComponent(`/contact/${encodeURIComponent(String(to))}`)}`); return; }
        if (res.status === 403) {
          let code = '';
          try { const j = await res.json(); code = String(j?.error || ''); } catch {}
          const known = (['email_disabled','post_only','friends_only','blocked','recipient_not_approved'] as const).find(c => c === code);
          if (known) { setError(titleFor(known)); setErrorCode(known); }
          else { setError('You cannot contact this user at this time.'); setErrorCode(null); }
          setLoading(false);
          return;
        }
        if (!res.ok) { setError(`Failed to load recipient (${res.status})`); setLoading(false); return; }
        const data = await res.json();
        setRecipient(data);
        const h = Number(data?.cooldownHoursRemaining || data?.CooldownHoursRemaining || 0);
        setCooldownHours(h);
        if (h > 0) {
          const until = Date.now() + Math.max(0, h) * 3600 * 1000;
          setCooldownUntil(until);
        }
        // ?? not ||: a 0 at the cap must reach disabledByPlan on page load.
        setMonthlyRemaining(Number(data?.monthlyRemaining ?? data?.MonthlyRemaining ?? 9999));
      } catch (e: any) {
        setError(e?.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, [toParam, router]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!recipient) return;
    if (sending) return; // guard against double-submits
    try {
      setError(null);
      setSending(true);
      const res = await apiFetch(`/api/contact/${encodeURIComponent(String(recipient.personId || recipient.PersonId || '') )}`, {
        method: 'POST',
        body: JSON.stringify({ message, context: context || null, contextId: contextId || null }),
      });
      if (res.status === 401) { router.push(`/login?returnTo=${encodeURIComponent(`/contact/${encodeURIComponent(String(recipient.personId || recipient.PersonId || ''))}`)}`); return; }
      if (res.status === 403) {
        let code = '';
        try { const j = await res.json(); code = String(j?.error || ''); } catch {}
        if (code === 'email_disabled') { setError(titleFor('email_disabled')); setErrorCode('email_disabled'); }
        else if (code === 'post_only') { setError(titleFor('post_only')); setErrorCode('post_only'); }
        else if (code === 'friends_only') { setError(titleFor('friends_only')); setErrorCode('friends_only'); }
        else if (code === 'blocked') { setError(titleFor('blocked')); setErrorCode('blocked'); }
        else if (code === 'recipient_not_approved') { setError(titleFor('recipient_not_approved')); setErrorCode('recipient_not_approved'); }
        else { setError('You cannot contact this user at this time.'); setErrorCode(null); }
        return;
      }
      if (res.status === 429) {
        const j = await res.json().catch(()=>({}));
        const h = Number(j?.retryAfterHours || 0);
        setCooldownHours(h);
        setRetryAfterHours(h);
        const until = Date.now() + Math.max(0, h) * 3600 * 1000;
        setCooldownUntil(until);
        const code = String(j?.code || 'message_limit');
        if (code === 'direct_cooldown') { setErrorCode('direct_cooldown'); }
        else { setErrorCode('message_limit'); }
        if (j?.code === 'message_limit') {
          // The api's monthly cap (ContactController answers 429
          // { code: 'message_limit' }), not a pace limit: hold Send and show
          // the monthly limit line instead of "too quickly".
          setMonthlyRemaining(0);
        } else {
          setRateLimitedMsg('You are sending messages too quickly. Please wait before trying again.');
        }
        // Do not set `error` so we keep the compose UI visible
        return;
      }
      if (res.status === 409) {
        setError('You have reached your monthly message limit.');
        return;
      }
      if (!res.ok) { const t = await res.text().catch(()=> ''); setError(`Failed to send (${res.status}) ${t}`); return; }
      setSent(true);
      // Contacting a poster about their post IS the application-submitted moment.
      if (context === 'post' && contextId) {
        gaEvent('application_submitted', { post_id: contextId, via: 'contact' });
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to send');
    } finally { setSending(false); }
  }

  // Client-side anti-spam guard
  const blocked = /dollmate|pornhouse\s/i.test(message);
  // Cooldown timer updater
  React.useEffect(() => {
    if (!cooldownUntil) { setCooldownLeft(0); return; }
    const tick = () => {
      const leftMs = cooldownUntil - Date.now();
      setCooldownLeft(leftMs > 0 ? Math.ceil(leftMs / 1000) : 0);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [cooldownUntil]);

  const disabledByCooldown = (cooldownHours > 0) || (cooldownLeft > 0);
  const disabledByPlan = monthlyRemaining <= 0;
  const disableSend = sending || sent || blocked || disabledByCooldown || disabledByPlan;
  const remaining = Math.max(0, 2000 - (message?.length || 0));
  const warnCounter = remaining <= 200; // 90% used

  if (loading) return <div className="container mx-auto px-4 py-6">Loading…</div>;
  if (error) return (
    <div className="container mx-auto px-4 py-6">
      <LiveAnnouncer message={error} mode="assertive" />
      {errorCode ? (
        <PolicyBanner
          code={errorCode}
          hours={retryAfterHours}
          onOpenProfile={() => {
            const pid = String(personId || toParam || '').trim();
            if (pid) {
              const slug = recipient ? normalizeSlug(recipient.displayName || recipient.stageName || '') : '';
              const href = (slug && slug.length >= 3) ? `/u/${slug}` : `/details/${encodeURIComponent(pid)}`;
              try { router.push(href); } catch { window.location.href = href; }
            }
          }}
        />
      ) : (
        <div className="text-destructive">{error}</div>
      )}
    </div>
  );
  if (!recipient) return <div className="container mx-auto px-4 py-6">Recipient not found.</div>;

  const avatarUrl = recipient.avatarUrl || recipient.AvatarUrl || '/img/default-avatar.png';
  const displayName = recipient.displayName || recipient.DisplayName || '';

  return (
    <div className="container mx-auto px-4 py-6 space-y-4">
      {sending && <LiveAnnouncer message="Sending message…" mode="polite" />}
      {sent && (
        <>
          <LiveAnnouncer message="Message sent – We’ve recorded this contact in your message history." mode="assertive" />
          <div role="alert" aria-live="assertive" tabIndex={-1} className="rounded-md border border-border bg-emerald-50 dark:bg-emerald-900/10 text-foreground p-3" id="contact-success-banner">
            <strong>Message sent</strong> – We’ve recorded this contact in your message history.
          </div>
        </>
      )}
      {/* Title box */}
      <div className="rounded-2xl border border-border bg-rose-50 dark:bg-rose-900/10 p-4 sm:p-6">
        <h1 className="text-xl sm:text-2xl font-extrabold">Contact {displayName}</h1>
        {context && (
          <p className="mt-1 text-xs text-muted-foreground">
            Origin: <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent text-accent-foreground">
              {context}{contextId ? ` #${contextId}` : ''}
            </span>
          </p>
        )}
      </div>

      {/* Grid: compose + recipient */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Compose box */}
        <div className="lg:col-span-8">
          <div className="rounded-2xl border border-border bg-indigo-50 dark:bg-indigo-900/10 p-4 sm:p-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium">Subject</label>
                <Input className="mt-1" value="Contact From Juicystarz" readOnly />
              </div>
              <form onSubmit={onSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium">Message</label>
                  <Textarea className="mt-1 min-h-48" value={message} onChange={(e)=>setMessage(e.target.value)} required maxLength={2000} aria-invalid={false} />
                  <div className="flex justify-between items-center mt-1">
                    <div className="text-xs text-muted-foreground">Max 2000 characters. Include how to reach you if appropriate.</div>
                    <div className={`text-xs ${warnCounter ? 'text-amber-600' : 'text-muted-foreground'}`}>{remaining} left</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button type="submit" variant="primary" disabled={disableSend} className="hidden md:inline-flex">{sent ? 'Sent' : (sending ? 'Sending…' : 'Send Message Now')}</Button>
                  <Button type="submit" variant="primary" disabled={disableSend} className="md:hidden inline-flex w-full justify-center">{sent ? 'Sent' : (sending ? 'Sending…' : 'Send Message Now')}</Button>
                  <Button type="button" variant="outline" onClick={() => {
                    const recipSlug = normalizeSlug(recipient.displayName || recipient.DisplayName || '');
                    const pid = recipient.personId || recipient.PersonId;
                    router.push(recipSlug ? `/u/${recipSlug}` : `/details/${encodeURIComponent(String(pid))}`);
                  }}>Back to Profile</Button>
                </div>
                {blocked && <div className="text-xs text-muted-foreground">Message appears to include prohibited content.</div>}
                {disabledByCooldown && (
                  <div className="text-xs text-muted-foreground">
                    {cooldownLeft > 0 ? (
                      <>Please wait {Math.floor(cooldownLeft/60)}m {cooldownLeft%60}s before sending another email.</>
                    ) : (
                      <>Wait {Math.ceil(cooldownHours)} hour(s) before sending another email.</>
                    )}
                  </div>
                )}
                {disabledByPlan && <div className="text-xs text-muted-foreground" data-testid="contact-monthly-limit">Monthly limit reached. {LIMIT_RESET_TEXT}</div>}
                <div className="text-xs text-muted-foreground">We’ll email this message to the recipient. Replies may appear in your inbox and email.</div>
                {/* Only show fatal errors inline; rate-limit displays neutral guidance above */}
                {error && <div className="text-sm text-destructive" role="alert" aria-live="assertive">{error}</div>}
                {rateLimitedMsg && (
                  <div className="text-xs text-amber-700" aria-live="polite">{rateLimitedMsg}</div>
                )}
              </form>
            </div>
          </div>
        </div>

        {/* Recipient box */}
        <div className="lg:col-span-4">
          <div className="rounded-2xl border border-border bg-emerald-50 dark:bg-emerald-900/10 p-4 sm:p-6">
            <div className="mb-4">
              <MiniTalentCard
                id={recipient.personId || recipient.PersonId}
                name={recipient.displayName}
                avatarUrl={avatarUrl}
                subtitle={[recipient.sex, recipient.ethnicity, recipient.age ? `${recipient.age}` : null].filter(Boolean).join(' · ')}
              />
            </div>
            <div>
              <div className="text-sm font-medium mb-1.5">Recent Contact History</div>
              {Array.isArray(recipient.history) && recipient.history.length > 0 ? (
                <ul className="text-xs text-muted-foreground space-y-1 max-h-40 overflow-y-auto pr-1">
                  {recipient.history.map((t: string, i: number) => (
                    <li key={i}>{t}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-muted-foreground">No prior messages.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
